import React, { useCallback, useMemo, useRef } from 'react';
import {
    useTable,
    useBlockLayout,
    HeaderGroup,
    useSortBy,
    useGroupBy,
    useExpanded,
    Row,
} from 'react-table';
import { useDeepCompareEffect } from 'use-deep-compare';

import {
    CompactNumberCell,
    EmptyCell,
    EstimatedLossCell,
    IconInfoCircle,
    InnerHeader,
    GroupedRowsWithSubHeaders,
    TableFooterGroup,
    TableHeader,
    TableHeaderGroup,
    TruncatedTextCell,
} from '@trqk/components';
import {
    aggregateNumberRanges,
    createFuzzyTextFilter,
    locale,
} from '@trqk/utils';
import { useStateWithCallback } from '@trqk/hooks';

import {
    COLUMN_IDS,
    columnWidths,
    footerHeight,
    headerHeight,
    subHeaderHeight,
    subRowHeight,
    defaultColumnWidth,
    rowHeight,
    tableHeight,
} from '../constants';
import {
    ChannelNameCell,
    GroupedChannelNameCell,
    GroupedTrackNameCell,
    TrackNameCell,
    ProgramAndSeriesCell,
} from './cell';
import { ChannelFooter, EstimatedLossFooter, SumFooter } from './footer';
import { groupTrackNameByMatchState } from '../utils';

interface ViewGapAnalysisTableProps {
    /**
     * Array of {@link GapReportAudio} objects to render as table rows
     */
    gapReportAudios: GapReportAudio[];
    /**
     * The statement ID that was used to generate the Gap Analysis report
     */
    statementId: Maybe<string>;
}

/* eslint-disable react/jsx-props-no-spreading */

/**
 * Renders the data for a single Gap Analysis report that has been completed. Rows are grouped by
 * channel name and groups can be expanded / collapsed.
 * @param props - {@link ViewGapAnalysisTableProps}
 * @returns JSX
 */
export const ViewGapAnalysisTable = ({
    gapReportAudios,
    statementId,
}: ViewGapAnalysisTableProps): JSX.Element => {
    // When table data is updated, prevent table state from automatically resetting
    const skipAutoResetRef = useRef(false);
    const setSkipAutoResetRefToFalse = useCallback(() => {
        skipAutoResetRef.current = false;
    }, []);
    const [data, setData] = useStateWithCallback(
        gapReportAudios,
        setSkipAutoResetRefToFalse,
    );

    // Use deep equality to compare the state of gapReportAudios and only update table data if it
    // changes
    useDeepCompareEffect(() => {
        skipAutoResetRef.current = true;

        setData(gapReportAudios);
    }, [gapReportAudios]);

    // When our cell renderer calls updateMyData, use the item id, columnId and new value to update
    // the original data
    const updateMyData: TableDataUpdater = useCallback(
        (itemId, columnId, value) => {
            skipAutoResetRef.current = true;

            setData(prevData =>
                prevData.map(row => {
                    if (row.gap_audio_id === itemId && columnId !== undefined) {
                        return {
                            ...row,
                            [columnId]: value,
                        };
                    }

                    return row;
                }),
            );
        },
        [setData, skipAutoResetRef],
    );

    // Calculate table width based on the available space in the viewport. The table will grow
    // horizontally to fill the viewport, but it will not cause the page to scroll.
    const tableElementRef = useRef<HTMLDivElement>(null);

    const defaultColumn = useMemo(
        () => ({
            width: defaultColumnWidth,
        }),
        [],
    );

    const columns = useMemo(
        () => [
            {
                id: COLUMN_IDS.CHANNEL_NAME,
                Header: TableHeader,
                InnerHeader: EmptyCell,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_VIEWER_TABLE.HEADERS
                        .CHANNEL_NAME,
                accessor: ({
                    channel_name_detection,
                }: GapReportAudio): string => channel_name_detection,
                Grouped: GroupedChannelNameCell,
                Cell: ChannelNameCell,
                Footer: ChannelFooter,
                width: columnWidths[COLUMN_IDS.CHANNEL_NAME],
            },
            {
                id: COLUMN_IDS.TRACK_NAME,
                Header: EmptyCell,
                InnerHeader,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_VIEWER_TABLE.HEADERS
                        .TRACK_NAME,
                accessor: ({
                    audio_filename,
                    channel_name_detection,
                    detection_title,
                    matched_work_title,
                }: GapReportAudio): GapReportAudioTrackNameCellValue => ({
                    audioFilename: audio_filename ?? null,
                    channel: channel_name_detection,
                    detectionTitle: detection_title ?? null,
                    matchedWorkTitle: matched_work_title ?? null,
                    statementId,
                }),
                Aggregated: EmptyCell,
                Grouped: GroupedTrackNameCell,
                Cell: TrackNameCell,
                width: columnWidths[COLUMN_IDS.TRACK_NAME],
            },
            {
                id: COLUMN_IDS.PROGRAM_AND_SERIES,
                Header: EmptyCell,
                InnerHeader,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_VIEWER_TABLE.HEADERS
                        .PROGRAM_AND_SERIES,
                accessor: ({
                    matched_work_program,
                    matched_work_series,
                }: GapReportAudio): GapReportAudioProgramAndSeriesCellValue => ({
                    matched_work_program: matched_work_program ?? null,
                    matched_work_series: matched_work_series ?? null,
                }),
                Aggregated: EmptyCell,
                Grouped: EmptyCell,
                Cell: ProgramAndSeriesCell,
                width: columnWidths[COLUMN_IDS.PROGRAM_AND_SERIES],
            },
            {
                id: COLUMN_IDS.SURVEY_TYPE,
                Header: EmptyCell,
                InnerHeader,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_VIEWER_TABLE.HEADERS
                        .SURVEY_TYPE,
                accessor: ({
                    matched_work_survey_type,
                }: GapReportAudio): Maybe<string> =>
                    matched_work_survey_type ?? null,
                Aggregated: EmptyCell,
                Grouped: EmptyCell,
                Cell: TruncatedTextCell,
                width: columnWidths[COLUMN_IDS.SURVEY_TYPE],
            },
            {
                id: COLUMN_IDS.USE_CODE,
                Header: EmptyCell,
                InnerHeader,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_VIEWER_TABLE.HEADERS
                        .USE_CODE,
                accessor: ({
                    matched_work_use_code,
                }: GapReportAudio): Maybe<string> =>
                    matched_work_use_code ?? null,
                Aggregated: EmptyCell,
                Grouped: EmptyCell,
                Cell: TruncatedTextCell,
                width: columnWidths[COLUMN_IDS.USE_CODE],
            },
            {
                id: COLUMN_IDS.DETECTIONS,
                Header: TableHeader,
                InnerHeader: EmptyCell,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_VIEWER_TABLE.HEADERS
                        .DETECTIONS,
                accessor: ({ detection_count }: GapReportAudio): number =>
                    detection_count ?? 0,
                aggregate: 'sum',
                Grouped: EmptyCell,
                Cell: CompactNumberCell,
                Footer: SumFooter,
                width: columnWidths[COLUMN_IDS.DETECTIONS],
            },
            {
                id: COLUMN_IDS.GAP,
                Header: TableHeader,
                InnerHeader: EmptyCell,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_VIEWER_TABLE.HEADERS.GAP,
                accessor: ({
                    detection_count,
                    matched_work_performances,
                }: GapReportAudio): number =>
                    (detection_count ?? 0) - (matched_work_performances ?? 0),
                aggregate: 'sum',
                Grouped: EmptyCell,
                Cell: CompactNumberCell,
                Footer: SumFooter,
                width: columnWidths[COLUMN_IDS.GAP],
            },
            {
                id: COLUMN_IDS.PERFORMANCES,
                Header: TableHeader,
                InnerHeader: EmptyCell,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_VIEWER_TABLE.HEADERS
                        .PERFORMANCES,
                accessor: ({
                    matched_work_performances,
                }: GapReportAudio): number => matched_work_performances ?? 0,
                aggregate: 'sum',
                Grouped: EmptyCell,
                Cell: CompactNumberCell,
                Footer: SumFooter,
                width: columnWidths[COLUMN_IDS.PERFORMANCES],
            },
            {
                id: COLUMN_IDS.ESTIMATED_LOSS,
                Header: TableHeader,
                InnerHeader: EmptyCell,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_VIEWER_TABLE.HEADERS
                        .ESTIMATED_LOSS,
                accessor: ({
                    estimated_loss_max,
                    estimated_loss_min,
                }: GapReportAudio): NumberRange => ({
                    max: estimated_loss_max ?? 0,
                    min: estimated_loss_min ?? 0,
                }),
                sortType: (
                    rowA: Row<GapReportAudio>,
                    rowB: Row<GapReportAudio>,
                ): number => {
                    const estimatedLossMaxA =
                        rowA?.original?.estimated_loss_max ?? 0;
                    const estimatedLossMaxB =
                        rowB?.original?.estimated_loss_max ?? 0;

                    if (estimatedLossMaxA > estimatedLossMaxB) {
                        return 1;
                    }

                    if (estimatedLossMaxA < estimatedLossMaxB) {
                        return -1;
                    }

                    return 0;
                },
                aggregate: aggregateNumberRanges,
                Grouped: EmptyCell,
                Cell: EstimatedLossCell,
                Footer: EstimatedLossFooter,
                width: columnWidths[COLUMN_IDS.ESTIMATED_LOSS],
            },
        ],
        [updateMyData, statementId],
    );

    const filterTypes = useMemo(
        () => ({
            titleFilter: createFuzzyTextFilter({
                keys: ['values.track.title'],
            }),
        }),
        [],
    );

    const groupByFn = useCallback(groupTrackNameByMatchState, []);

    const {
        flatRows,
        footerGroups,
        getTableBodyProps,
        getTableProps,
        headerGroups,
        prepareRow,
    } = useTable<GapReportAudio>(
        {
            columns,
            data,
            defaultColumn,
            filterTypes,
            initialState: {
                groupBy: [COLUMN_IDS.CHANNEL_NAME, COLUMN_IDS.TRACK_NAME],
                sortBy: [
                    {
                        id: COLUMN_IDS.CHANNEL_NAME,
                        desc: false,
                    },
                    {
                        id: COLUMN_IDS.GAP,
                        desc: true,
                    },
                    {
                        id: COLUMN_IDS.TRACK_NAME,
                        desc: false,
                    },
                ],
            },
            groupByFn,
            globalFilter: filterTypes.titleFilter,
            autoResetPage: !skipAutoResetRef.current,
            autoResetExpanded: !skipAutoResetRef.current,
            autoResetGroupBy: !skipAutoResetRef.current,
            autoResetSelectedRows: !skipAutoResetRef.current,
            autoResetSortBy: !skipAutoResetRef.current,
            autoResetFilters: !skipAutoResetRef.current,
            autoResetGlobalFilter: !skipAutoResetRef.current,
            autoResetRowState: !skipAutoResetRef.current,
        },
        useBlockLayout,
        useGroupBy,
        useSortBy,
        useExpanded,
    );

    return (
        <>
            <div
                ref={tableElementRef}
                {...getTableProps()}
                className="font-normal trqk-table"
            >
                <div
                    style={{
                        height: headerHeight,
                    }}
                    className="flex items-end"
                >
                    {headerGroups.map(
                        (
                            headerGroup: HeaderGroup<GapReportAudio>,
                        ): JSX.Element => {
                            const { key: headerGroupKey } =
                                headerGroup.getHeaderGroupProps();

                            return (
                                <TableHeaderGroup<GapReportAudio>
                                    key={headerGroupKey}
                                    headerGroup={headerGroup}
                                />
                            );
                        },
                    )}
                </div>
                <div
                    {...getTableBodyProps()}
                    className="overflow-x-hidden overflow-y-auto"
                    style={{
                        height: tableHeight - headerHeight - footerHeight,
                    }}
                >
                    {flatRows.length === 0 && (
                        <div
                            className="flex items-center justify-center italic text-tertiary-400"
                            style={{ height: 350 }}
                        >
                            <div className="flex flex-col items-center justify-center">
                                <div className="flex items-center">
                                    <IconInfoCircle
                                        addClasses="mr-1"
                                        width={20}
                                        fillColor="primary-500"
                                    />
                                    {
                                        locale.enUS.GAP_ANALYSIS
                                            .REPORT_VIEWER_TABLE
                                            .CURRENT_DATA_EMPTY
                                    }
                                </div>
                            </div>
                        </div>
                    )}
                    {flatRows.length > 0 && (
                        <GroupedRowsWithSubHeaders<GapReportAudio>
                            expandingGroupById={COLUMN_IDS.CHANNEL_NAME}
                            flatRows={flatRows}
                            subHeaderHeight={subHeaderHeight}
                            subRowHeight={subRowHeight}
                            rowHeight={rowHeight}
                            prepareRow={prepareRow}
                        />
                    )}
                </div>
            </div>
            <div
                className="flex items-center w-full text-sm font-semibold border border-tertiary-400"
                style={{ height: footerHeight }}
            >
                {footerGroups.map(
                    (footerGroup: HeaderGroup<GapReportAudio>): JSX.Element => {
                        const { key: footerGroupKey } =
                            footerGroup.getFooterGroupProps();

                        return (
                            <TableFooterGroup<GapReportAudio>
                                key={footerGroupKey}
                                footerGroup={footerGroup}
                            />
                        );
                    },
                )}
            </div>
        </>
    );
};
